<script lang="ts" setup>
const { $directus, $readItems } = useNuxtApp()
const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const { dt } = useDirectusTranslation()

const categories = [
  {
    key: 'plunge',
    name: 'Plunge < 5M',
  },
  {
    key: 'city',
    name: 'City 6-7M',
  },
  {
    key: 'regular',
    name: 'Regular 8-10M',
  },
  {
    key: 'family',
    name: 'Family 11-12M',
  },
  {
    key: 'signature',
    name: 'Starline Signature',
  },
]

const selectedCategory = ref<string | null>(null)

const selectedCategories = computed({
  get: () => [selectedCategory.value],
  set: (value) => {
    selectedCategory.value = value.at(-1) ?? null
  },
})

const { data } = await useAsyncData(
  'pool_index',
  () => $directus.request($readItems('pools', {
    sort: 'sort',
    filter: {
      status: {
        _eq: 'published',
      },
    },
    fields: [
      'id',
      'slug',
      'listing_image',
      'listing_image_hover',
      'categories',
      'color_scheme',
      {
        listing_translations: ['*'],
      },
    ],
  })) as Promise<{
    id: number
    slug: string
    listing_image: string
    listing_image_hover: string
    color_scheme: 'default' | 'signature'
    categories: string[]
    listing_translations: DirectusTranslation<{
      id: number
      name: string
      teaser_description: {
        text: string
      }[]
    }>
  }[]>,
  {
    // watch: [selectedCategory],
  },
)

if (route.query.category) {
  const urlQuery = route.query.category as string
  if (categories.map(category => category.key).includes(urlQuery))
    selectedCategory.value = route.query.category as string
}

watch(selectedCategory, () => {
  router.replace({
    query: {
      ...route.query,
      category: selectedCategory.value ?? undefined,
    },
  })
})

const filteredPools = computed(() => {
  if (!data.value)
    return []
  if (selectedCategory.value === null)
    return data.value
  return data?.value.filter((pool) => {
    return pool.categories.includes(selectedCategory.value!)
  })
})
</script>

<template>
  <BlockBase>
    <Container class="mb-6rem mt-8rem">
      <h2 class="text-center text-balance text-2.5rem tracking-px font-title">
        {{ t('pool.choose_your_dimensions') }}
      </h2>
    </Container>
    <div class="relative">
      <!-- Border -->
      <Container class="absolute inset-0 border border-neutral-darkGray" />
      <!-- Gradient over border -->
      <div class="bg-gradient absolute inset-x-0 top-0 h-350px" />
      <!-- White background -->
      <BlockBase is-light class="absolute inset-x-0 bottom-0 top-350px bg-neutral-white" />
      <Container class="absolute relative inset-x-0 flex flex-wrap items-center justify-center gap-1rem -top-2rem">
        <label
          v-for="category in categories" :key="category.key"
          class="inline-block cursor-pointer select-none bg-starline-secondary px-1.6rem py-.8rem text-1.3rem tracking-[.4rem] uppercase transition duration-300ms focus-within:outline hover:ring-starline-primary"
          :class="{
            'shadow-starline ring-3px ring-starline-primary relative': selectedCategory === category.key,
            'shadow-none ring-px ring-neutral-darkGray': selectedCategory !== category.key,

          }"
        >
          {{ category.name }}
          <input v-model="selectedCategories" type="checkbox" :value="category.key" class="hidden">
        </label>
      </Container>
      <Container class="relative min-h-350px px-6rem pt-6rem">
        <div v-auto-animate class="grid grid-cols-1 gap-4rem lg:grid-cols-2 xl:grid-cols-3">
          <p v-if="!filteredPools?.length" class="pb-6rem pt-4rem text-center text-1.6rem lg:col-span-2 xl:col-span-3">
            {{ t('pool.index_empty') }}
          </p>
          <template v-for="pool in filteredPools" :key="pool.id">
            <NuxtLinkLocale
              :to="`/pools/${pool.slug}`"
              class="group relative"
              :class="pool.color_scheme === 'signature' ? 'bg-signature-black text-white' : 'text-black bg-white'"
            >
              <div class="relative overflow-hidden">
                <NuxtImg
                  loading="lazy"
                  provider="directus" :src="pool.listing_image" height="400" width="400" class="block h-400px w-full object-cover transition-300 group-hover:scale-105"
                />
                <div class="absolute inset-0 h-400px scale-105 object-cover opacity-0 transition-300 group-hover:scale-100 group-hover:opacity-100">
                  <NuxtImg
                    loading="lazy"
                    provider="directus" height="400" width="400" class="h-full w-full object-cover" :src="pool.listing_image_hover"
                  />
                </div>
              </div>
              <div
                class="absolute left-0 top-0 w-full flex"
                :class="pool.color_scheme === 'signature'
                  ? 'text-signature'
                  : 'text-black' "
              >
                <div
                  class="grid h-6rem place-items-center px-2rem transition"
                  :class="pool.color_scheme === 'signature'
                    ? 'bg-signature-black text-signature group-hover:bg-signature-black/60'
                    : 'bg-neutral-white text-black group-hover:bg-neutral-white/60'"
                >
                  <h3
                    class="whitespace-nowrap text-1.6rem font-title transition-all"
                    :class="{
                      'text-neutral-gray group-hover:text-neutral-black': pool.color_scheme !== 'signature',
                    }"
                  >
                    {{ dt(pool.listing_translations)?.name }}
                  </h3>
                </div>
                <div class="grid grid-cols-[0fr] w-full transition-all group-hover:grid-cols-[1fr]">
                  <div
                    class="grid grid-cols-[1fr_min-content] min-w-0 items-center gap-2rem overflow-hidden"
                    :class="pool.color_scheme === 'signature'
                      ? 'bg-signature-black/60'
                      : 'bg-neutral-white/60' "
                  >
                    <div
                      class="h-px w-full"
                      :class="pool.color_scheme === 'signature'
                        ? 'bg-signature'
                        : 'bg-black' "
                    />

                    <IconArrowRight class="mr-2rem size-2.5rem" />
                  </div>
                </div>
              </div>
              <div class="relative">
                <ul v-if="dt(pool.listing_translations)?.teaser_description.length" class="px-2rem py-1rem">
                  <li v-for="item, index in dt(pool.listing_translations)?.teaser_description" :key="index" class="flex gap-4">
                    <div
                      class="mt-.8rem h-px w-3rem flex-shrink-0"
                      :class="pool.color_scheme === 'signature'
                        ? 'bg-signature'
                        : 'bg-black' "
                    />

                    <p class="font-body text-balance text-1.2rem leading-2.2rem">
                      {{ item.text }}
                    </p>
                  </li>
                </ul>
                <div v-if="pool.color_scheme === 'signature'" class="absolute inset-x-0 bottom-full h-100px from-transparent to-signature-black bg-gradient-to-b" />
              </div>
            </NuxtLinkLocale>
          </template>
        </div>
      </Container>
    </div>
  </BlockBase>
</template>

<style scoped>
.bg-gradient {
  background-image: linear-gradient(to bottom, transparent 0%, #000b1a 50%);
}
</style>
